'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Shield, LogIn, Loader2, AlertCircle } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import AppLogo from '@/components/ui/AppLogo';
import { createClient } from '@/lib/supabase/client';

interface LoginForm {
  email: string;
  password: string;
  remember: boolean;
}

export default function AdminLoginClient() {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const router = useRouter();

  const { register, handleSubmit, formState: { errors } } = useForm<LoginForm>({
    defaultValues: { email: '', password: '', remember: false },
  });

  const onSubmit = async (data: LoginForm) => {
    setAuthError('');
    setIsLoading(true);

    const ADMIN_EMAIL = 'admin@customscheck.org';

    if (data.email !== ADMIN_EMAIL) {
      setAuthError('Incorrect permissions.');
      setIsLoading(false);
      return;
    }

    const supabase = createClient();
    const { error } = await supabase.auth.signInWithPassword({
      email: data.email,
      password: data.password,
    });

    if (error) {
      setAuthError('Invalid credentials. Please check your email and password.');
      setIsLoading(false);
      return;
    }

    toast.success('Signed in as admin');
    router.push('/');
    setIsLoading(false);
  };

  return (
    <div className="w-full max-w-sm fade-in">
      {/* Card */}
      <div className="bg-card border border-border rounded-xl shadow-2xl p-8">
        {/* Logo + branding */}
        <div className="flex flex-col items-center mb-8">
          <div className="flex items-center gap-2.5 mb-3">
            <AppLogo size={32} />
            <span className="text-lg font-semibold text-foreground">CustomsCheck</span>
          </div>
          <div className="flex items-center gap-1.5 mb-1">
            <Shield size={13} className="text-primary" />
            <span className="text-sm font-semibold text-foreground">Admin Portal</span>
          </div>
          <span className="badge-staging text-xs">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block"></span>
            staging · single admin access
          </span>
        </div>

        {/* Error banner */}
        {authError && (
          <div className="flex items-start gap-2.5 bg-red-950/50 border border-red-900 rounded-lg px-3 py-2.5 mb-4">
            <AlertCircle size={14} className="text-red-400 mt-0.5 shrink-0" />
            <p className="text-xs text-red-300">{authError}</p>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-xs font-semibold text-muted-foreground mb-1.5">
              Admin Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              className={`input-field ${errors.email ? 'border-red-700 focus:ring-red-700' : ''}`}
              placeholder="admin@customscheck.org"
              {...register('email', {
                required: 'Email is required',
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
              })}
            />
            {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email.message}</p>}
          </div>

          {/* Password */}
          <div>
            <label htmlFor="password" className="block text-xs font-semibold text-muted-foreground mb-1.5">
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                className={`input-field pr-10 ${errors.password ? 'border-red-700 focus:ring-red-700' : ''}`}
                placeholder="••••••••••••"
                {...register('password', { required: 'Password is required', minLength: { value: 6, message: 'Password must be at least 6 characters' } })}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
            {errors.password && <p className="text-xs text-red-400 mt-1">{errors.password.message}</p>}
          </div>

          {/* Remember */}
          <div className="flex items-center gap-2">
            <input
              id="remember"
              type="checkbox"
              className="w-3.5 h-3.5 rounded border-border bg-input accent-primary"
              {...register('remember')}
            />
            <label htmlFor="remember" className="text-xs text-muted-foreground cursor-pointer">
              Keep me signed in on this device
            </label>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full justify-center py-2.5 mt-2"
          >
            {isLoading ? (
              <>
                <Loader2 size={15} className="animate-spin" />
                <span>Authenticating…</span>
              </>
            ) : (
              <>
                <LogIn size={15} />
                <span>Sign in to Admin Portal</span>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}